import PropTypes from 'prop-types';
import React, {useState, useMemo} from 'react';
import {debounce as _debounce} from 'lodash';

import {FiltersWrapper} from '@appComponents/HOCFiltersWrapper';
import useQueryCustom from '@utils/hooks/useQueryCustom';
import {mapPropertyLicencesForDropdown} from '@utils/propertyLicence';
import {convertToInt} from '@utils/helpers';
import {GetDataForSearchingFiltersInLcppClientNotificationsLog} from '@graphql/clientProductLcpp/query';
import {GetTournamentCategoriesFilteredBySports} from '@graphql/tournamentCategory/query';
import {GetTournamentsFilteredByTournamentCategories} from '@graphql/tournament/query';
import {PropertyLicencesFilter} from '@utils/commonFilters/PropertyLicencesFilter';
import {SearchFilter} from '@utils/commonFilters/SearchFilter';
import {defineNextState} from '@utils/filters';
import {DateRangeFilter} from '@utils/commonFilters/DateRangeFilter';
import {SportsFilter} from '@utils/commonFilters/SportsFilter';
import {TournamentsFilter} from '@utils/commonFilters/TournamentsFilter';
import {TournamentCategoriesFilter} from '@utils/commonFilters/TournamentCategoriesFilter';
import {getTodayDate, getEndOfTodayDate} from '@utils/date';

import {prepareSportsDropdownData, showErrorNotificationMessage} from '../utils/lcppClientNotificationLog';

export const LcppClientNotificationLogFilters = ({
    loading,
    form,
    restoreFilterValues,
    restoreFilterDateValues,
    filters,
}) => {
    const [hasSelectedSport, setHasSelectedSport] = useState(false),
        [hasSelectedTournamentCategory, setHasSelectedTournamentCategory] = useState(false),
        [tournamentCategoriesLoading, setTournamentCategoriesLoading] = useState(false),
        [tournamentsLoading, setTournamentsLoading] = useState(false),
        [sportsIds, setSportsIds] = useState([]),
        [tournamentCategoryIds, setTournamentCategoryIds] = useState([]),
        [skipTournamentCategoriesFetch, setSkipTournamentCategoriesFetch] = useState(true),
        [skipTournamentsFetch, setSkipTournamentsFetch] = useState(true),
        [refetchTournamentCategories, setRefetchTournamentCategories] = useState(null),
        [dateConfig, setDateConfig] = useState({fromMaxDate: null, fromMaxTime: null, toMinDate: null, toMinTime: null}),
        [refetchTournaments, setRefetchTournaments] = useState(null);

    const {
        data: {
            propertyLicencesForDropdown,
            propertyLicences,
            sports,
        } = {},
        error: errorFetchingDataForSearchingFilters,
        loading: isDataForSearchingFiltersLoading = true,
    } = useQueryCustom({
        query: GetDataForSearchingFiltersInLcppClientNotificationsLog,
    });

    const sportsWithOurMediaRights = prepareSportsDropdownData(
        isDataForSearchingFiltersLoading,
        propertyLicences,
        sports
    );

    /* get sports start */
    const {
        data: {tournamentCategories} = {},
        error: errorFetchingTournamentCategoriesData,
        loading: isTournamentCategoriesDropdownDataLoading = true,
    } = useQueryCustom({
        query: GetTournamentCategoriesFilteredBySports,
        options: {
            variables: {
                sports: sportsIds,
            },
            skip: skipTournamentCategoriesFetch,
            refetch: refetchTournamentCategories,
        },
    });

    const getTournamentCategories = _debounce(() => {
        setSkipTournamentCategoriesFetch(false);
        setRefetchTournamentCategories(new Date());
        setTournamentCategoriesLoading(false);
    }, 1000);

    const onSportChange = (event, value) => {
        if (0 < value.length) {
            setHasSelectedSport(true);
            setTournamentCategoriesLoading(true);
            setSportsIds(value.map(sport => convertToInt(sport)));
            getTournamentCategories();
        } else {
            form.changeValue('tournamentCategories', null);
            setHasSelectedSport(false);
        }
    };
    /* get the sports end */

    const onChange = (date, name) => {
        setDateConfig(prev => {
            let nextState = { ...prev };
            defineNextState(nextState, prev, date, name);

            return nextState;
        });
    };

    /* get tournament start */
    const getTournaments = _debounce(() => {
        setSkipTournamentsFetch(false);
        setRefetchTournaments(new Date());
        setTournamentsLoading(false);
    }, 1000);

    const onTournamentCategoryChange = (event, value) => {
        if (0 < value.length) {
            setHasSelectedTournamentCategory(true);
            setTournamentsLoading(true);
            setTournamentCategoryIds(value.map(tournamentCategory => convertToInt(tournamentCategory)));
            getTournaments();
        } else {
            form.changeValue('tournaments', null);
            setHasSelectedTournamentCategory(false);
        }
    };

    const {
        data: {tournaments} = {},
        error: errorFetchingTournamentsData,
        loading: isTournamentsDropdownDataLoading = true,
    } = useQueryCustom({
        query: GetTournamentsFilteredByTournamentCategories,
        options: {
            variables: {
                tournamentCategories: tournamentCategoryIds,
            },
            skip: skipTournamentsFetch,
            refetch: refetchTournaments,
        },
    });
    /* get the tournament end */

    const propertyLicencesOptions = !isDataForSearchingFiltersLoading
            ? mapPropertyLicencesForDropdown(propertyLicencesForDropdown)
            : [],
        errorOccurredWhileFetchingData = errorFetchingDataForSearchingFilters
        || errorFetchingTournamentCategoriesData
        || errorFetchingTournamentsData;

    useMemo(()=> {
        errorOccurredWhileFetchingData && showErrorNotificationMessage();
    }, [errorOccurredWhileFetchingData]);

    return (
        <div className='content__filters'>
            <SearchFilter
                loading={loading}
                filters={filters.LcppClientNotificationLog}
                restoreFilterValues={restoreFilterValues}
            />
            <DateRangeFilter
                loading={loading}
                filters={filters.LcppClientNotificationLog}
                restoreFilterDateValues={restoreFilterDateValues}
                onChange={onChange}
                showTime={true}
                dateConfig={dateConfig}
                configuration={{
                    shouldCloseOnSelect: false,
                    timeFormat: 'HH:mm',
                    timeIntervals: 5,
                }}
            />
            <PropertyLicencesFilter
                propertyLicences={propertyLicencesOptions}
                loading={isDataForSearchingFiltersLoading}
                filters={filters.LcppClientNotificationLog}
                restoreFilterValues={restoreFilterValues}
            />
            <SportsFilter
                sports={sportsWithOurMediaRights}
                loading={isDataForSearchingFiltersLoading}
                filters={filters.LcppClientNotificationLog}
                restoreFilterValues={restoreFilterValues}
                onChangeValue={onSportChange}
            />
            <TournamentCategoriesFilter
                tournamentCategories={tournamentCategories}
                loading={isTournamentCategoriesDropdownDataLoading || tournamentCategoriesLoading}
                filters={filters.LcppClientNotificationLog}
                restoreFilterValues={restoreFilterValues}
                hidden={!hasSelectedSport}
                onChangeValue={onTournamentCategoryChange}
            />
            <TournamentsFilter
                tournaments={tournaments || []}
                loading={isTournamentsDropdownDataLoading || tournamentsLoading}
                filters={filters.LcppClientNotificationLog}
                restoreFilterValues={restoreFilterValues}
                hidden={!hasSelectedTournamentCategory}
                disabled={tournamentCategoriesLoading || tournamentsLoading || loading}
            />
        </div>
    );
};

LcppClientNotificationLogFilters.propTypes = {
    filters: PropTypes.shape({
        LcppClientNotificationLog: PropTypes.object.isRequired,
    }),
    form: PropTypes.object.isRequired,
    loading: PropTypes.bool.isRequired,
    restoreFilterValues: PropTypes.func.isRequired,
    restoreFilterDateValues: PropTypes.func.isRequired,
    onChange: PropTypes.func,
};

export default FiltersWrapper(LcppClientNotificationLogFilters, {
    name: 'LcppClientNotificationLog',
    initialValues: {
        from: getTodayDate(),
        to: getEndOfTodayDate(),
    },
});
